import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import Step from 'components/global/Step'
import VoucherTypePicker from './VoucherTypePicker'
import QuantityPickerSection from 'components/shared/QuantityPickerSection'
import { CARD_VOUCHERS_LIMIT, VALUE_VOUCHERS_LIMIT } from 'constants/limits'
import AddItem from 'components/shared/AddItem'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useVouchers } from 'store/vouchers-slice/hooks'
import { setDeliveryType, setErrors } from 'store/order-slice'
import { useApp } from 'store/steps'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { setIsErrorModalOpened } from 'store/general-slice'
import steps from 'index'
import {
    addBlankVoucher,
    removeLastVoucher,
    setVouchersType,
    setVouchersVisible,
} from 'store/vouchers-slice'
import { useCart } from 'store/cart-slice/hooks'
import { useAutoScroll } from 'utils/hooks'
import VouchersConfigurationsList from './VouchersConfigurationsList'
import { pushDataLayer } from 'helpers/analytics'
import { VoucherType } from 'store/api-slice/types'

const VouchersConfiguration = () => {
    const [predefinedParams, setPredefinedParams] = useSearchParams()
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { stepIndex, navigateToStep } = useApp()
    const { setErrorMessages } = useErrorMessages()
    const { isProcessing, addVouchersToCart, clearItemsFromCart } =
        useVouchers()
    const { isCartEmpty } = useCart()
    const { calculateScroll } = useAutoScroll()

    const { vouchersType, quantity, vouchersBoxes, areVouchersVisible } =
        useAppSelector((state) => state.vouchers)

    const vouchersLimit = useMemo(
        () =>
            vouchersType === 'product'
                ? CARD_VOUCHERS_LIMIT
                : VALUE_VOUCHERS_LIMIT,
        [vouchersType]
    )
    const isQuantityUnderLimit = vouchersBoxes.length < vouchersLimit
    const isContinuePossible = useMemo(
        () =>
            vouchersBoxes.every(
                ({ amount, personDetail, cardDetail, voucherSignature }) =>
                    (amount || (personDetail && cardDetail)) &&
                    voucherSignature?.length
            ),
        [vouchersBoxes]
    )

    const handleAddVoucherSlot = () => {
        dispatch(addBlankVoucher())
        calculateScroll({
            trigerringSection: 'vouchers_configuration',
            trigerringAction: 'add',
        })
    }

    const handleRemoveVoucherSlot = () => dispatch(removeLastVoucher())

    const handleProceed = () => {
        dispatch(setVouchersVisible())
        calculateScroll({
            trigerringSection: 'vouchers_configuration',
            trigerringAction: 'add',
        })
    }

    const handleAddVouchersToCart = async () => {
        dispatch(setErrors([]))

        // < analytics
        pushDataLayer({
            event: 'funnelStep',
            event_category: 'vouchers',
            event_action: 'vouchers_params_selected',
            event_label: '',
        })
        // analytics >

        addVouchersToCart().then((response) => {
            const { isSuccess, messages } = response

            if (isSuccess) {
                const nextStepId = steps[stepIndex + 1].id
                navigateToStep({ stepId: nextStepId })
            } else {
                if (messages) {
                    const error = { messages }

                    if (
                        messages.some((error) =>
                            error.property_path.includes('detail')
                        )
                    )
                        setErrorMessages(error)
                    else dispatch(setIsErrorModalOpened(true))
                }
            }
        })
    }

    useEffect(() => {
        if (!areVouchersVisible) return

        // < analytics
        pushDataLayer({
            event: 'productPresented',
            ecommerce: {
                items: [
                    {
                        item_name: 'Voucher',
                        item_brand: 'PoP',
                    },
                ],
            },
        })
        // analytics >
    }, [areVouchersVisible])

    useEffect(() => {
        if (!isCartEmpty && !!vouchersBoxes.length) {
            clearItemsFromCart()
            dispatch(setDeliveryType('email'))
        }

        // ?type=amount || ?type=product
        // read url params predefining voucher type,
        // clear it from path and auto-trigger corresponding controls
        const predefinedType = predefinedParams.get('type')
        if (predefinedType && ['amount', 'product'].includes(predefinedType)) {
            predefinedParams.delete('type')
            setPredefinedParams(predefinedParams)
            dispatch(setVouchersType(predefinedType as VoucherType))
            calculateScroll({
                trigerringSection: 'vouchers_configuration',
                trigerringAction: 'choose_type',
                targetId: 'voucherQuantityPicker',
            })
        }
    }, [])

    return (
        <>
            <Step
                title={formatMessage({
                    id: 'global.steps.caption_vouchers_configuration',
                    defaultMessage: 'Konfiguracja vouchera',
                })}
                isButtonDisabled={!isContinuePossible}
                isButtonLoading={isProcessing}
                isButtonVisible={areVouchersVisible}
                onButtonClick={handleAddVouchersToCart}
            >
                <VoucherTypePicker />

                {vouchersType && (
                    <QuantityPickerSection
                        id="voucherQuantityPicker"
                        sectionHeader={formatMessage({
                            id: 'vouchers.config.quantity_caption',
                            defaultMessage: 'Wybierz liczbę voucherów',
                        })}
                        minValue={1}
                        maxValue={vouchersLimit}
                        value={quantity}
                        onAdd={handleAddVoucherSlot}
                        onSubstract={handleRemoveVoucherSlot}
                        onSubmit={handleProceed}
                        testIdPrefix="vouchers"
                    />
                )}

                {areVouchersVisible && <VouchersConfigurationsList />}

                {areVouchersVisible &&
                    isContinuePossible &&
                    isQuantityUnderLimit && (
                        <AddItem
                            onClick={handleAddVoucherSlot}
                            testId="vouchers-button-add-another"
                        >
                            <FormattedMessage
                                id="vouchers.config.btn_add_another_voucher"
                                defaultMessage="Dodaj kolejny voucher"
                            />
                        </AddItem>
                    )}
            </Step>
        </>
    )
}

export default VouchersConfiguration
